/**
 * Host-network failure detection.
 *
 * A leaf module (no imports) so both `sentry.ts` and `errors.ts` can use it
 * without a cycle — `api-client.ts` imports `sentry.ts`, and `errors.ts` imports
 * `api-client.ts`, so the detector can't live in either of those.
 *
 * undici's global `fetch` (the CLI's only HTTP transport) rejects host-network
 * failures — offline, DNS miss, connection refused/reset, transport drop — as a
 * `TypeError: fetch failed`, with the specific socket/DNS code on `.cause.code`.
 * These are the user's network, not a WayAI bug: they stay off Sentry/autofix
 * (see `shouldReportToSentry`) and get a "check your connection" hint instead of
 * a bare `fetch failed` (see `friendlyHint`). Detecting at these two chokepoints
 * — which every CLI error funnels through — covers every raw `fetch()` site
 * (api-client, auth/login, resource downloads) without per-site rewrapping.
 */

/**
 * Node socket/DNS error codes that mean "the host couldn't reach the network".
 * Deliberately excludes `EPIPE`: a broken *stdout* pipe (`wayai … | head`) raises
 * `EPIPE` too, so it isn't unambiguously a network failure. A genuine network
 * `EPIPE` from undici still surfaces as `TypeError: fetch failed` (matched by the
 * message check in `isNetworkError`, regardless of `.cause.code`), so dropping it
 * here loses no detection.
 */
const NETWORK_ERROR_CODES = new Set([
  'ECONNREFUSED',
  'ENOTFOUND',
  'ETIMEDOUT',
  'ECONNRESET',
  'EAI_AGAIN',
  'ECONNABORTED',
  'EHOSTUNREACH',
  'ENETUNREACH',
]);

function errorCode(value: unknown): string | undefined {
  if (value && typeof value === 'object' && 'code' in value) {
    const code = (value as { code?: unknown }).code;
    if (typeof code === 'string') return code;
  }
  return undefined;
}

/**
 * True when `err` is a host-network failure rather than an HTTP response or a
 * logic bug. Matches undici's canonical `TypeError: fetch failed`, plus a
 * belt-and-suspenders check for a known socket/DNS code on the error or its
 * `.cause` (a direct Node socket error, or a non-undici fetch polyfill).
 */
export function isNetworkError(err: unknown): boolean {
  if (err instanceof TypeError && err.message === 'fetch failed') return true;
  if (!err || typeof err !== 'object') return false;
  // `err` is now a non-null object, so reading `.cause` is an honest cast —
  // mirroring the guard in `errorCode` rather than a wider `as {...} | null`.
  const code = errorCode(err) ?? errorCode((err as { cause?: unknown }).cause);
  return code !== undefined && NETWORK_ERROR_CODES.has(code);
}
